import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNoSidebar } from '../../hooks/useNoSidebar';
import ProductShowcase from '../items/HomePage/ProductShowcase';
import styles from './HomePage.module.css';

// Enhanced SVG icons for feature pillars with gradients
const PPRAIcon = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
    <defs>
      <linearGradient id="ppraGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{stopColor: '#1976d2', stopOpacity: 1}} />
        <stop offset="100%" style={{stopColor: '#42a5f5', stopOpacity: 1}} />
      </linearGradient>
    </defs>
    <circle cx="30" cy="30" r="28" fill="url(#ppraGrad)" opacity="0.1"/>
    <path d="M30 10 L45 20 L45 40 L30 50 L15 40 L15 20 Z" stroke="url(#ppraGrad)" strokeWidth="3" fill="none"/>
    <path d="M22 30h16M30 22v16" stroke="url(#ppraGrad)" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const FICAIcon = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
    <defs>
      <linearGradient id="ficaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{stopColor: '#00796b', stopOpacity: 1}} />
        <stop offset="100%" style={{stopColor: '#26a69a', stopOpacity: 1}} />
      </linearGradient>
    </defs>
    <circle cx="30" cy="30" r="28" fill="url(#ficaGrad)" opacity="0.1"/>
    <rect x="15" y="15" width="30" height="30" rx="6" stroke="url(#ficaGrad)" strokeWidth="3" fill="none"/>
    <path d="M25 25 L35 35 M35 25 L25 35" stroke="url(#ficaGrad)" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="30" cy="30" r="8" stroke="url(#ficaGrad)" strokeWidth="2" fill="none"/>
  </svg>
);

const POPIAIcon = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
    <defs>
      <linearGradient id="popiaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{stopColor: '#ff9800', stopOpacity: 1}} />
        <stop offset="100%" style={{stopColor: '#ffb74d', stopOpacity: 1}} />
      </linearGradient>
    </defs>
    <circle cx="30" cy="30" r="28" fill="url(#popiaGrad)" opacity="0.1"/>
    <path d="M30 15 L45 25 L45 45 L30 55 L15 45 L15 25 Z" stroke="url(#popiaGrad)" strokeWidth="3" fill="none"/>
    <path d="M30 25 v10 M25 32 h10" stroke="url(#popiaGrad)" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="30" cy="40" r="3" fill="url(#popiaGrad)"/>
  </svg>
);

// Animated hero illustration: a compliance checklist auto-completing while an
// audit-readiness ring fills to 100%. All motion is CSS-driven (see
// HomePage.module.css) and pauses under prefers-reduced-motion, where every
// element rests in its finished ("100% ready") state.
const RING_C = 150.8; // circumference of the r=24 progress ring
const CHECK_LEN = 34; // length of the seal checkmark path

const ChecklistRow = ({ y, width, tickClass }: { y: number; width: number; tickClass: string }) => (
  <g transform={`translate(128 ${y})`}>
    {/* always-visible empty checkbox */}
    <rect x="0" y="0" width="22" height="22" rx="6" fill="#eef4fb" stroke="url(#heroGrad1)" strokeWidth="2" />
    {/* ticked state (animated in) */}
    <g className={tickClass}>
      <rect x="0" y="0" width="22" height="22" rx="6" fill="#43a047" />
      <path d="M5 11.5 L9.5 16 L17 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
    <rect x="34" y="6" width={width} height="9" rx="4.5" fill="#dbe7f4" />
  </g>
);

const HeroIllustration = () => (
  <svg width="450" height="400" viewBox="0 0 450 400" fill="none" className={styles.heroSvg}
    role="img" aria-label="A compliance checklist completing itself as an audit-readiness meter fills to 100 percent">
    <defs>
      <linearGradient id="heroGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1976d2" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#00796b" stopOpacity="0.8" />
      </linearGradient>
      <linearGradient id="heroGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00796b" />
        <stop offset="100%" stopColor="#1976d2" />
      </linearGradient>
      <linearGradient id="heroRing" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#42a5f5" />
        <stop offset="100%" stopColor="#26a69a" />
      </linearGradient>
    </defs>

    {/* Pulsing back glow */}
    <circle className={styles.heroGlow} cx="225" cy="200" r="165" fill="url(#heroGrad1)" opacity="0.14" />

    {/* Drifting accents */}
    <circle className={styles.heroDriftA} cx="366" cy="86" r="12" fill="#ff9800" opacity="0.55" />
    <circle className={styles.heroDriftB} cx="70" cy="250" r="16" fill="#1976d2" opacity="0.45" />
    <rect className={styles.heroDriftSquare} x="342" y="300" width="26" height="26" rx="7" fill="#00796b" opacity="0.4" />

    {/* Floating document group */}
    <g className={styles.heroFloat}>
      {/* Cards behind */}
      <rect x="132" y="58" width="200" height="252" rx="16" fill="#cbe7ff" opacity="0.4" transform="rotate(5 232 184)" />
      <rect x="98" y="66" width="200" height="252" rx="16" fill="#eaf6ff" opacity="0.65" transform="rotate(-3 198 192)" />

      {/* Main card */}
      <rect x="108" y="72" width="200" height="252" rx="16" fill="#ffffff" />
      <rect x="128" y="94" width="112" height="12" rx="6" fill="url(#heroGrad2)" />
      <circle cx="292" cy="100" r="7" fill="#ff9800" opacity="0.9" />

      {/* Checklist */}
      <ChecklistRow y={134} width={140} tickClass={styles.heroTick1} />
      <ChecklistRow y={172} width={116} tickClass={styles.heroTick2} />
      <ChecklistRow y={210} width={132} tickClass={styles.heroTick3} />

      {/* Audit-readiness ring */}
      <g transform="translate(248 276)">
        <circle r="24" fill="none" stroke="#e3ecf5" strokeWidth="6" />
        <circle
          className={styles.heroRingProg}
          r="24"
          fill="none"
          stroke="url(#heroRing)"
          strokeWidth="6"
          strokeLinecap="round"
          transform="rotate(-90)"
          strokeDasharray={RING_C}
          strokeDashoffset={0}
        />
        <path
          className={styles.heroRingCheck}
          d="M-9 0 L-3 6.5 L10 -8"
          stroke="#1a7a63"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          strokeDasharray={CHECK_LEN}
          strokeDashoffset={0}
        />
      </g>
      <rect x="286" y="264" width="14" height="8" rx="4" fill="#dbe7f4" />
      <rect x="286" y="280" width="24" height="8" rx="4" fill="#eef4fb" />
    </g>
  </svg>
);

const HomePage: React.FC = () => {
  useNoSidebar();
  const navigate = useNavigate();

  const handleStartTrial = () => {
    // Navigate to signup tab of LoginRegister page
    navigate('/LoginRegister?tab=signup');
  };

  const handleSeeHowItWorks = () => {
    document.getElementById('showcase')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.homepage}>
      {/* 1. Above-the-Fold Section */}
      <section className={styles.aboveFold}>
        <div className={styles.heroContentWrapper}>
          <div className={styles.heroTextBlock}>
            <h1 className={styles.headline}>Stop Fearing the Audit. Get 100% PPRA, FICA, and POPIA Ready.</h1>
            <p className={styles.subHeadline}>Realty Genie is the all-in-one compliance platform that automates filing, guides agents, and ensures your South African agency passes every audit, every time.</p>
            <div className={styles.ctaGroup}>
              <button className={styles.primaryCta} onClick={handleStartTrial}>Start Your 7-Day Free Trial</button>
              <button className={styles.secondaryCta} onClick={handleSeeHowItWorks}>See How It Works</button>
            </div>
            <div className={styles.trustSignal}>
              <em>Trusted by Principals across Gauteng and Western Cape.</em>
            </div>
          </div>
          <div className={styles.heroIllustrationBlock}>
            <HeroIllustration />
          </div>
        </div>
      </section>

      {/* 2. Problem & Solution Section */}
      <section className={styles.problemSolution}>
        <h2>The Compliance Burden Is Costing You Time and Money.</h2>
        <p>Are your agents wasting hours manually filing FICA documents? Is your mandate storage ready for a surprise PPRA inspection? Stop letting regulatory stress consume your operations. <strong>Realty Genie</strong> takes the complexity out of compliance, allowing your agency to focus purely on sales.</p>
        <div className={styles.painSolutionGrid}>
          <div>
            <h3>Fear of <strong>Massive Fines</strong> and Reputational Damage.</h3>
            <p><strong>Audit-Proof Documentation:</strong> Automatically files documents in the correct, legally compliant structure.</p>
          </div>
          <div>
            <h3>Agents don't know <em>which</em> documents to collect.</h3>
            <p><strong>Guided Agent Workflow:</strong> Step-by-step instructions on FICA verification, PPRA forms, and POPIA consent.</p>
          </div>
          <div>
            <h3>Scrambling to prepare files when the <strong>PPRA</strong> calls.</h3>
            <p><strong>One-Click Audit Report:</strong> Generate a full, indexed compliance file instantly for any audit.</p>
          </div>
        </div>
      </section>

      {/* 3. Feature Breakdown Section */}
      <section className={styles.featureBreakdown}>
        <div className={styles.featureColumn}>
          <div className={styles.featureIcon}><PPRAIcon /></div>
          <h2>PPRA Audit Excellence</h2>
          <ul>
            <li>Automated Mandate Indexing</li>
            <li>Time-Stamped Audit Trails</li>
            <li>Principal Dashboard</li>
          </ul>
        </div>
        <div className={styles.featureColumn}>
          <div className={styles.featureIcon}><FICAIcon /></div>
          <h2>FICA Risk Elimination</h2>
          <ul>
            <li>Digital RMCP Framework</li>
            <li>Simplified Beneficial Owner ID</li>
            <li>5-Year Secure Retention</li>
          </ul>
        </div>
        <div className={styles.featureColumn}>
          <div className={styles.featureIcon}><POPIAIcon /></div>
          <h2>POPIA Compliance & Security</h2>
          <ul>
            <li>Secure Data Vault</li>
            <li>Consent Management</li>
            <li>End-of-Life Data Disposal</li>
          </ul>
        </div>
      </section>

      {/* 4. Interactive Product Showcase */}
      <ProductShowcase />

      {/* 5. Social Proof & Testimonial Section */}
      {/* <section className={styles.socialProof}>
        <h2>"The Compliance Burden is Gone."</h2>
        <blockquote>
          <div className={styles.avatarPlaceholder}></div>
          "Before Realty Genie, we spent two days compiling files for an audit. With this platform, it took us literally 60 seconds. It's a game-changer for our agency's risk management."<br />
          <span>- [Name], Principal, [Agency Name], Johannesburg</span>
        </blockquote>
        <div className={styles.counters}>
          <span><strong>10,000+</strong> Documents Filed</span>
          <span><strong>30+</strong> Agencies Trusting Us</span>
        </div>
      </section> */}

      {/* 5. Final Conversion Section */}
      <section className={styles.finalConversion}>
        <h2>Ready to Automate Your Compliance?</h2>
        <p>Don't let non-compliance be the downfall of your business in 2026. Join the growing number of South African agencies using the power of <strong>Realty Genie</strong>.</p>
        <button className={styles.finalCta} onClick={handleStartTrial}>Start Your 7-Day Free Trial Now</button>
      </section>
    </div>
  );
};

export default HomePage;
